<script setup lang="ts">
import { Status4B4Enum, type TableOrderInfo } from "@innoclouds/api";
import dayjs from "dayjs";
import { useTranslation } from "i18next-vue";
import { computed } from "vue";
import { useMainStore } from "#/store/main";
import { formatPrice } from "#/utils/i18next";

const props = defineProps<{
	orders: TableOrderInfo[];
}>();

const emit = defineEmits<{
	select: [order: TableOrderInfo];
	back: [];
}>();

const { t } = useTranslation();
const storeMain = useMainStore();

const currencySymbol = computed(() => storeMain.config_lib?.currency_symbol);

const statusLabels = computed<Partial<Record<Status4B4Enum, string>>>(() => ({
	[Status4B4Enum.confirmed]: t(`table-service.active-orders.status.confirmed`),
	[Status4B4Enum.waiting_payment]: t(`table-service.active-orders.status.waiting-payment`),
	[Status4B4Enum.partial_paid]: t(`table-service.active-orders.status.partial-paid`),
}));

const orderTitle = (order: TableOrderInfo) => t(`cart.table.order-number`, { number: order.number ?? "—" });

const orderMeta = (order: TableOrderInfo) => {
	const openedAt = t(`table-service.active-orders.opened-at`, { time: dayjs(order.created_at).format("HH:mm") });
	const status = order.status ? statusLabels.value[order.status] : undefined;

	return status ? `${status} · ${openedAt}` : openedAt;
};
</script>

<template>
	<div class="active_orders_list">
		<button v-for="order in props.orders" :key="order.id" type="button" class="active_orders_list_item" @click="emit('select', order)">
			<div class="active_orders_list_item_text">
				<p class="active_orders_list_item_title">{{ orderTitle(order) }}</p>
				<p class="active_orders_list_item_meta">{{ orderMeta(order) }}</p>
			</div>
			<p class="active_orders_list_item_price">{{ formatPrice(order.total_cost ?? 0, currencySymbol) }}</p>
		</button>

		<button type="button" class="table_active_orders_back_text" @click="emit('back')">← {{ t(`table-service.button.back`) }}</button>
	</div>
</template>

<style lang="scss">
  @use './activeOrdersList.scss';
</style>
